import { Trans } from '@lingui/macro'
import { Button, Stack, Tooltip, Typography, styled } from '@mui/material'
import { ApplicationTypes, round } from 'jms-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useCallback } from 'react'

import { PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'

export const SummaryDetailRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
}))

const PriceLabel = styled(Typography)(() => ({
  fontSize: '1.0rem',
  fontWeight: 400,
  lineHeight: '1.25rem',
  letterSpacing: -0.3,
}))

const PriceValue = styled(Typography)(() => ({
  fontSize: '1.1rem',
  fontWeight: 400,
  lineHeight: '1.25rem',
  letterSpacing: -0.3,
}))

const TotalPriceLabel = styled(Typography)(() => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  letterSpacing: -0.3,
}))

const TotalPriceValue = styled(Typography)(() => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  lineHeight: '1.25rem',
  letterSpacing: -0.3,
}))

const Currency = styled('span')(({ theme }) => ({
  marginInlineEnd: theme.spacing(0.25),
}))

const priceFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface CustomPriceFooterProps {
  appliancePrice: number
  cabinetsAndPartsPrice: number
  itemCount: number
}

export const CustomPriceFooter = (props: CustomPriceFooterProps) => {
  const { appliancePrice, cabinetsAndPartsPrice, itemCount } = props

  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()

  const activeNavigationState = useObservableEagerState(navigationSystem.activeNavigationState$)
  const showEditCabinetsButton = activeNavigationState === ApplicationTypes.NavigationState.Room && itemCount === 0

  const handleAddCabinet = useCallback(() => {
    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.RoomContent)])
  }, [navigationSystem, storeSystem])

  return (
    <Stack direction="column" gap={2}>
      <SummaryDetailRow>
        <PriceLabel>
          <Trans>Cabinets</Trans>
        </PriceLabel>
        <PriceValue>
          <Currency title="US Dollar sign">$</Currency>
          {priceFormatter.format(cabinetsAndPartsPrice)}
        </PriceValue>
      </SummaryDetailRow>
      <SummaryDetailRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PriceLabel>
            <Trans>Appliances</Trans>
          </PriceLabel>
          <Tooltip title="Appliances are sold separately" placement="top" arrow>
            {/* Tooltip can't wrap SVG icon directly. */}
            <div style={{ lineHeight: 0 }}>
              <PhxIcon name="Info" sx={{ color: 'GrayText' }} />
            </div>
          </Tooltip>
        </div>
        <PriceValue>
          <Currency title="US Dollar sign">$</Currency>
          {priceFormatter.format(appliancePrice)}
        </PriceValue>
      </SummaryDetailRow>
      <SummaryDetailRow>
        <TotalPriceLabel>
          <strong>Total price</strong>
        </TotalPriceLabel>
        <TotalPriceValue>
          <strong>
            <Currency title="US Dollar sign">$</Currency>
            {priceFormatter.format(round(appliancePrice + cabinetsAndPartsPrice, 2))}
          </strong>
        </TotalPriceValue>
      </SummaryDetailRow>
      {showEditCabinetsButton && (
        <Button sx={{ width: '100%' }} variant="contained" color="primary" size="large" onClick={handleAddCabinet}>
          Edit cabinets
        </Button>
      )}
    </Stack>
  )
}
